import React from 'react';
import { Box, Typography, Grid, Container, Avatar } from '@mui/material';
//import { Box, Typography, Grid, Button, Card, CardContent, Container, Avatar, useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from '../hooks/useTranslation';
import { useLanguage } from '../contexts/LanguageContext';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
// import BrushIcon from '@mui/icons-material/Brush';
// import PaletteIcon from '@mui/icons-material/Palette';
// import SchoolIcon from '@mui/icons-material/School';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { keyframes } from '@mui/system';
import { SEOHead } from '../components/SEOHead';

const floatingAnimation = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-15px) rotate(5deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// const GlassCard = styled(Card)({
//   height: '100%',
//   background: 'rgba(255, 255, 255, 0.85)',
//   backdropFilter: 'blur(12px)',
//   WebkitBackdropFilter: 'blur(12px)',
//   border: '1px solid rgba(255, 255, 255, 0.3)',
//   transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
//   '&:hover': {
//     transform: 'translateY(-8px) scale(1.02)',
//     boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
//     background: 'rgba(255, 255, 255, 0.95)',
//   },
//   boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
//   borderRadius: '32px',
//   overflow: 'hidden',
//   position: 'relative',
//   '&::before': {
//     content: '""',
//     position: 'absolute',
//     top: 0,
//     left: 0,
//     right: 0,
//     bottom: 0,
//     background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
//     zIndex: 1,
//   }
// });

// const rotatingAnimation = {
//   rotate: [0, 360],
//   transition: {
//     duration: 8,
//     repeat: Infinity,
//     ease: "linear"
//   }
// };

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 320,
  height: 320,
  border: '6px solid white',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
  animation: `${floatingAnimation} 4s ease-in-out infinite`,
  [theme.breakpoints.down('sm')]: {
    width: 240,
    height: 240,
  }
}));

const GradientText = styled(Typography)(({ theme }) => ({
  background: theme.palette.primary.main,
  backgroundSize: '200% 200%',
  animation: `${gradientAnimation} 5s ease infinite`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  textFillColor: 'transparent',
}));

export const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const isRTL = currentLang === 'he';
  // const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // SEO structured data for homepage
  const homepageStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": isRTL ? "ענבל ארט מייקרס" : "Inbal Art Makers",
    "alternateName": isRTL ? "Inbal Art Makers" : "ענבל ארט מייקרס",
    "url": "https://www.inbalart.co.il",
    "description": isRTL 
      ? "סדנאות קריקט מקצועיות, הדרכות עיצוב דיגיטלי וקורסי תכשיטנות. למדו מהמומחית ענבל לוין בהרצליה."
      : "Professional Cricut workshops, digital design tutorials, and jewelry making classes. Learn from expert Inbal Levin in Herzliya.",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://www.inbalart.co.il/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    },
    "mainEntity": {
      "@type": "Organization",
      "name": isRTL ? "ענבל ארט מייקרס" : "Inbal Art Makers",
      "url": "https://www.inbalart.co.il",
      "logo": "https://res.cloudinary.com/dkaie0i1s/image/upload/v1745213182/Screenshot_2025-04-21_at_8.25.34_qy7sks.webp",
      "description": isRTL 
        ? "סדנאות קריקט מקצועיות, הדרכות עיצוב דיגיטלי וקורסי תכשיטנות. למדו מהמומחית ענבל לוין בהרצליה."
        : "Professional Cricut workshops, digital design tutorials, and jewelry making classes. Learn from expert Inbal Levin in Herzliya.",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Yigal Alon St",
        "addressLocality": isRTL ? "הרצליה" : "Herzliya",
        "addressCountry": "IL"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+972-54-7798844",
        "contactType": "customer service",
        "email": "inbal.artmakers@gmail.com"
      },
      "sameAs": [
        "https://www.facebook.com/inbalartmakers",
        "https://www.instagram.com/inbalartmakers"
      ]
    }
  };

  return (
    <>
      <SEOHead 
        title={isRTL ? "ענבל ארט מייקרס - סדנאות קריקט ועיצוב דיגיטלי" : "Inbal Art Makers - Cricut Workshops & Digital Design"}
        description={isRTL 
          ? "סדנאות קריקט מקצועיות, הדרכות עיצוב דיגיטלי וקורסי תכשיטנות. למדו מהמומחית ענבל לוין בהרצליה. Professional Cricut workshops with Inbal Levin."
          : "Professional Cricut workshops, digital design tutorials, and jewelry making classes. Learn from expert Inbal Levin in Herzliya. סדנאות קריקט מקצועיות עם ענבל לוין בהרצליה."
        }
        keywords={isRTL
          ? "סדנאות קריקט, קריקט ישראל, חיתוך דיגיטלי, תכשיטנות, עיצוב דיגיטלי, הרצליה, ענבל לוין, cricut israel, cricut workshops, digital cutting, jewelry making, digital design"
          : "cricut israel, cricut workshops, digital cutting, jewelry making, digital design, סדנאות קריקט, קריקט ישראל, חיתוך דיגיטלי, תכשיטנות, עיצוב דיגיטלי, הרצליה, ענבל לוין"
        }
        url="https://www.inbalart.co.il"
        structuredData={homepageStructuredData}
      />
      <Box dir={isRTL ? 'rtl' : 'ltr'}>
      {/* About Me Section */}
      <Box
        sx={{
          py: { xs: 8, md: 16 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
          }
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Grid container spacing={8} alignItems="center">
              <Grid item xs={12} md={5} sx={{ order: { xs: 1, md: isRTL ? 2 : 1 } }}>
                <Box sx={{ 
                  textAlign: 'center', 
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%'
                }}>
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                  >
                    <StyledAvatar
                      src="https://res.cloudinary.com/dkaie0i1s/image/upload/v1745213182/Screenshot_2025-04-21_at_8.25.34_qy7sks.webp"
                      alt="Profile"
                    />
                  </motion.div>
                  <motion.div
                    animate={{
                      scale: [1, 1.1, 1],
                      rotate: [0, 180, 360],
                    }}
                    transition={{
                      duration: 8,
                      repeat: Infinity,
                      ease: "linear"
                    }}
                    style={{
                      position: 'absolute',
                      top: '10%',
                      right: '10%',
                      zIndex: 1,
                    }}
                  >
                    <AutoAwesomeIcon sx={{ fontSize: 40, color: 'primary.main', opacity: 0.8 }} />
                  </motion.div>
                </Box>
              </Grid>
              <Grid item xs={12} md={7} sx={{ order: { xs: 2, md: isRTL ? 1 : 2 } }}>
                <motion.div
                  initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  <GradientText
                    variant="h2"
                    gutterBottom
                    sx={{
                      fontWeight: 800,
                      mb: 3,
                      fontSize: { xs: '2.5rem', md: '3.5rem' },
                      textAlign: { xs: 'center', md: 'left' },
                      lineHeight: 1.2,
                    }}
                  >
                    {t('pages.about.name')}
                  </GradientText>
                  <Typography 
                    variant="h5" 
                    component="h2" 
                    gutterBottom
                    sx={{
                      fontWeight: 600,
                      color: 'text.secondary',
                      mb: 3,
                      fontSize: { xs: '1.3rem', md: '1.8rem' },
                      textAlign: { xs: 'center', md: 'left' },
                    }}
                  >
                    {t('homepage.about.subtitle')}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    paragraph
                    sx={{ 
                      fontSize: { xs: '1.1rem', md: '1.3rem' },
                      lineHeight: 1.8,
                      color: 'text.secondary',
                      mb: 4,
                      textAlign: { xs: 'center', md: 'left' },
                    }}
                  >
                    {t('homepage.about.description')}
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ 
                      fontSize: { xs: '1.1rem', md: '1.2rem' },
                      lineHeight: 1.8,
                      color: 'text.secondary',
                      mb: 4,
                      textAlign: { xs: 'center', md: 'left' },
                    }}
                  >
                    {t('homepage.about.workshops.description')}
                  </Typography>
                </motion.div>
              </Grid>
            </Grid>
          </motion.div>
        </Container>
      </Box>
      </Box>
    </>
  );
}; 